<template>
  <teleport to="body" v-if="props.modelValue">
    <transition appear name="modal">
      <div class="og-modal" :style="modalStyle">
        <div :class="wrapperClass">
          <div class="modal-dialog" :class="dialogClass">
            <div class="modal-content" @click="stopImmediatePropagation">
              <template v-if="$slots.title || props.title">
                <div class="modal-header">
                  <template v-if="!$slots.title && props.title">
                    <h4 class="modal-title d-flex">
                      {{ props.title }}
                    </h4>
                  </template>
                  <template v-if="$slots.title">
                    <slot name="title" :modal="this"/>
                  </template>
                </div>
              </template>
              <div class="modal-body">
                <slot :modal="this"/>
              </div>
              <template v-if="$slots.footer">
                <div class="og-modal__footer">
                  <slot name="footer" :modal="this"/>
                </div>
              </template>

              <template v-if="props.closable">
                <s-button
                  aria-label="Close"
                  class="btn btn-link-hover text-black og-modal__close"
                  data-dismiss="modal"
                  @click="close"
                  style="z-index: 99"
                >
                  <span aria-hidden="true"><cross-icon2/></span>
                </s-button>
              </template>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </teleport>
</template>

<script setup>
  import CrossIcon2 from '@/components/icons/cross2';
  import { computed, watch, getCurrentInstance, onBeforeUnmount } from 'vue';
  import { stopImmediatePropagation } from '@/utils/handlers';
  import { useBackdropStore } from '@/stores/backdrop';

  const emit = defineEmits(['close', 'update:modelValue']);

  const props = defineProps({
    modelValue: {
      type: Boolean,
      default: true,
    },
    placement: {
      type: String,
      default: 'default',
      validator(value) {
        return ['default', 'aside-right'].includes(value);
      },
    },
    closable: {
      type: Boolean,
      default: true,
    },
    title: {
      type: String,
      default: null,
    },
    size: {
      type: String,
      default: '',
    },
    titleIcon: {
      type: String,
      default: null,
    },
    wrapperClasses: {
      type: Array,
      default: () => [],
    },
    class: {
      type: [String],
      default: '',
    },
  });

  const backdropStore = useBackdropStore();
  const zIndex = backdropStore.zIndex;
  const instance = getCurrentInstance();

  // save modal instance uid to determine if to lock/unlock screen scrollbar
  backdropStore.registerLayer(instance.uid);

  const modalStyle = computed(() => ({
    'z-index': zIndex,
  }));

  const wrapperClass = computed(() => {
    let list = ['og-modal__wrapper', `og-modal__wrapper--${props.placement}`];

    list.push(...props.wrapperClasses);

    if (props.class) {
      list = [...list, ...props.class.split(' ')];
    }

    return list;
  });

  const dialogClass = computed(() => {
    const list = [];

    if (props.size) {
      list.push(`modal-${props.size}`);
    }

    return list;
  });

  watch(() => props.modelValue, (newValue) => {
    // toggle modal layer (locks/unlocks screen scrollbar)
    backdropStore.toggleLayer(instance.uid, newValue);
  }, { immediate: true });

  onBeforeUnmount(() => backdropStore.unregister(instance.uid));

  function close() {
    if (!props.closable) {
      return;
    }

    forceClose();
  }

  function forceClose() {
    emit('update:modelValue', false);
    emit('close', this);
  }
</script>
